/**
 * Timesheet Settings Service
 * Manages organization-level timesheet configuration with localStorage persistence
 */

import { DEFAULT_TIMESHEET_SETTINGS } from '@/types/timesheetSettings';
import type { 
  OrganizationTimesheetSettings,
  TimesheetDeadlineSettings,
  UtilizationSettings,
  WorkingProfile,
  PolicyViolationSettings,
  PolicyLimits
} from '@/types/timesheetSettings';

const STORAGE_KEY = 'timetrack_org_settings';

/**
 * Get organization timesheet settings
 * Returns default settings if none are saved
 */
export function getOrganizationSettings(): OrganizationTimesheetSettings {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Merge with defaults to ensure all fields exist
      return {
        ...DEFAULT_TIMESHEET_SETTINGS,
        ...parsed,
        deadline: {
          ...DEFAULT_TIMESHEET_SETTINGS.deadline,
          ...parsed.deadline
        },
        workingProfile: {
          ...DEFAULT_TIMESHEET_SETTINGS.workingProfile,
          ...parsed.workingProfile,
          workingDays: {
            ...DEFAULT_TIMESHEET_SETTINGS.workingProfile.workingDays,
            ...parsed.workingProfile?.workingDays
          }
        },
        policyViolations: {
          ...DEFAULT_TIMESHEET_SETTINGS.policyViolations,
          ...parsed.policyViolations
        },
        policyLimits: parsed.policyLimits ? {
          ...DEFAULT_TIMESHEET_SETTINGS.policyLimits,
          ...parsed.policyLimits
        } : undefined,
        hoursLimits: {
          ...DEFAULT_TIMESHEET_SETTINGS.hoursLimits,
          ...parsed.hoursLimits
        },
        utilization: {
          ...DEFAULT_TIMESHEET_SETTINGS.utilization,
          ...parsed.utilization
        }
      };
    }
  } catch (error) {
    console.error('Error loading organization settings:', error);
  }
  
  return DEFAULT_TIMESHEET_SETTINGS;
}

/**
 * Save organization timesheet settings
 */
export function saveOrganizationSettings(settings: OrganizationTimesheetSettings): void {
  try {
    const toSave = {
      ...settings,
      updatedAt: new Date().toISOString()
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
  } catch (error) {
    console.error('Error saving organization settings:', error);
    throw new Error('Failed to save settings');
  }
}

/**
 * Update deadline settings only
 */
export function updateDeadlineSettings(deadline: Partial<TimesheetDeadlineSettings>): OrganizationTimesheetSettings {
  const current = getOrganizationSettings();
  const updated = {
    ...current,
    deadline: {
      ...current.deadline,
      ...deadline
    }
  };
  saveOrganizationSettings(updated);
  return updated;
}

/**
 * Update working hours settings (working profile, policy violations, and policy limits)
 */
export function updateWorkingHoursSettings(
  workingProfile: WorkingProfile,
  policyViolations: PolicyViolationSettings,
  policyLimits?: PolicyLimits
): OrganizationTimesheetSettings {
  const current = getOrganizationSettings();
  const updated = {
    ...current,
    workingProfile,
    policyViolations,
    policyLimits
  };
  saveOrganizationSettings(updated);
  return updated;
}

/**
 * Update utilization settings only
 */
export function updateUtilizationSettings(utilization: Partial<UtilizationSettings>): OrganizationTimesheetSettings {
  const current = getOrganizationSettings();
  const updated = {
    ...current,
    utilization: {
      ...current.utilization,
      ...utilization
    }
  };
  saveOrganizationSettings(updated);
  return updated;
}

/**
 * Reset to default settings
 */
export function resetToDefaults(): OrganizationTimesheetSettings {
  saveOrganizationSettings(DEFAULT_TIMESHEET_SETTINGS);
  return DEFAULT_TIMESHEET_SETTINGS;
}

/**
 * Get the deadline date for a given week
 * @param weekStart - Start date of the week (Monday)
 * @returns The deadline date for that week
 */
export function getDeadlineForWeek(weekStart: Date): Date {
  const settings = getOrganizationSettings();
  const deadline = new Date(weekStart);
  
  // Calculate days to add based on week start day and deadline day
  const daysToAdd = settings.deadline.deadlineDay;
  deadline.setDate(deadline.getDate() + daysToAdd);
  
  // Set the time
  const [hours, minutes] = settings.deadline.deadlineTime.split(':').map(Number);
  deadline.setHours(hours, minutes, 0, 0);
  
  return deadline;
}

/**
 * Check if a submission date is considered "on time"
 * @param submittedDate - When the timesheet was submitted
 * @param weekStart - Start date of the week
 * @returns true if on time (within grace period), false if late
 */
export function isSubmissionOnTime(submittedDate: Date, weekStart: Date): boolean {
  const deadline = getDeadlineForWeek(weekStart);
  const settings = getOrganizationSettings();
  
  // Add grace period to deadline
  const graceDeadline = new Date(deadline);
  graceDeadline.setDate(graceDeadline.getDate() + settings.deadline.gracePeriodDays);
  
  return submittedDate <= graceDeadline;
}

/**
 * Calculate days late for a submission
 * @param submittedDate - When the timesheet was submitted
 * @param weekStart - Start date of the week
 * @returns Number of days late (0 if on time, negative if early)
 */
export function getDaysLate(submittedDate: Date, weekStart: Date): number {
  const deadline = getDeadlineForWeek(weekStart);
  const diffMs = submittedDate.getTime() - deadline.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  return Math.max(0, diffDays);
}

/**
 * Get days until deadline for a given week
 * @param weekStart - Start date of the week
 * @param referenceDate - Date to calculate from (default: today)
 * @returns Number of days until deadline (negative if past deadline)
 */
export function getDaysUntilDeadline(weekStart: Date, referenceDate: Date = new Date()): number {
  const deadline = getDeadlineForWeek(weekStart);
  const diffMs = deadline.getTime() - referenceDate.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  return diffDays;
}

/**
 * Check if hours exceed daily limit
 */
export function exceedsDailyLimit(hours: number): boolean {
  const settings = getOrganizationSettings();
  return hours > settings.hoursLimits.dailyHoursLimit;
}

/**
 * Check if hours exceed weekly limit
 */
export function exceedsWeeklyLimit(hours: number): boolean {
  const settings = getOrganizationSettings();
  return hours > settings.hoursLimits.weeklyHoursLimit;
}

/**
 * Get target utilization rate
 */
export function getTargetUtilizationRate(): number {
  const settings = getOrganizationSettings();
  return settings.utilization.targetUtilizationRate;
}
